import React, { useState } from "react";
import { ActivityIndicator, Pressable, StyleSheet, View } from "react-native";
import { PencilSimpleIcon, XIcon } from "@phosphor-icons/react";

import CustomText from "../../../../../components/common/general/CustomText/CustomText";
import { colors } from "../../../../../styles/design-system";
import {
  ModernizedActivityEntityType,
  ModernizedDescriptionFormat,
  useGetModernizedModuleDetailQuery,
  useGetModernizedAssignmentDetailQuery,
  useGetModernizedAssessmentDetailQuery,
  useGetModernizedProjectDetailQuery,
  useGetModernizedOtherActivityDetailQuery,
  useUpdateModernizedActivityFieldsMutation,
} from "@axio/graphql/generated/graphql";
import SegmentedDiffView from "./SegmentedDiffView";
import type { StructuredFields, SegmentVariant } from "../types/reviewTypes";

interface GapAnalysisIssueEditorProps {
  courseId: string;
  entityId: string;
  entityType: string;
  entityTitle: string;
  onClose: () => void;
}

/**
 * Normalizes entity type string to ModernizedActivityEntityType enum value.
 * Handles various input formats (lowercase, uppercase, snake_case, camelCase).
 */
const normalizeEntityType = (type: string): ModernizedActivityEntityType => {
  const normalized = type.toLowerCase().replace(/_/g, "");

  const mapping: Record<string, ModernizedActivityEntityType> = {
    assessment: ModernizedActivityEntityType.Assessment,
    assignment: ModernizedActivityEntityType.Assignment,
    contentsection: ModernizedActivityEntityType.ContentSection,
    ilerecommendation: ModernizedActivityEntityType.IleRecommendation,
    module: ModernizedActivityEntityType.Module,
    otheractivity: ModernizedActivityEntityType.OtherActivity,
    project: ModernizedActivityEntityType.Project,
  };

  const result = mapping[normalized];

  if (!result) {
    console.warn(`[GapAnalysisIssueEditor] Unknown entityType: "${type}", falling back to OtherActivity`);
    return ModernizedActivityEntityType.OtherActivity;
  }

  return result;
};

function GapAnalysisIssueEditor({ courseId, entityId, entityType, entityTitle, onClose }: GapAnalysisIssueEditorProps) {
  const [isSaving, setIsSaving] = useState(false);

  // Normalize entityType to match ModernizedActivityEntityType enum
  const normalizedEntityType = normalizeEntityType(entityType);

  // Use the appropriate query based on entity type
  const moduleQuery = useGetModernizedModuleDetailQuery({
    variables: { entityId },
    skip: normalizedEntityType !== ModernizedActivityEntityType.Module,
  });

  const assignmentQuery = useGetModernizedAssignmentDetailQuery({
    variables: { entityId },
    skip: normalizedEntityType !== ModernizedActivityEntityType.Assignment,
  });

  const assessmentQuery = useGetModernizedAssessmentDetailQuery({
    variables: { entityId },
    skip: normalizedEntityType !== ModernizedActivityEntityType.Assessment,
  });

  const projectQuery = useGetModernizedProjectDetailQuery({
    variables: { entityId },
    skip: normalizedEntityType !== ModernizedActivityEntityType.Project,
  });

  const otherActivityQuery = useGetModernizedOtherActivityDetailQuery({
    variables: { entityId },
    skip: normalizedEntityType !== ModernizedActivityEntityType.OtherActivity,
  });

  // Select the active query based on entity type
  const activeQuery =
    normalizedEntityType === ModernizedActivityEntityType.Module
      ? moduleQuery
      : normalizedEntityType === ModernizedActivityEntityType.Assignment
        ? assignmentQuery
        : normalizedEntityType === ModernizedActivityEntityType.Assessment
          ? assessmentQuery
          : normalizedEntityType === ModernizedActivityEntityType.Project
            ? projectQuery
            : otherActivityQuery;

  const { data, loading, refetch } = activeQuery;

  const [updateFields] = useUpdateModernizedActivityFieldsMutation();

  // Extract activity from the appropriate query response
  const activity =
    normalizedEntityType === ModernizedActivityEntityType.Module
      ? data?.getModernizedModuleDetail?.activity
      : normalizedEntityType === ModernizedActivityEntityType.Assignment
        ? data?.getModernizedAssignmentDetail?.activity
        : normalizedEntityType === ModernizedActivityEntityType.Assessment
          ? data?.getModernizedAssessmentDetail?.activity
          : normalizedEntityType === ModernizedActivityEntityType.Project
            ? data?.getModernizedProjectDetail?.activity
            : data?.getModernizedOtherActivityDetail?.activity;

  const handleSave = async (editedFields: StructuredFields) => {
    if (!activity) return;

    setIsSaving(true);
    try {
      const result = await updateFields({
        variables: {
          courseId,
          entityId,
          entityType: normalizedEntityType,
          recommendedTitle: editedFields.title !== activity.title ? editedFields.title : undefined,
          recommendedDescription:
            editedFields.description !== (activity.descriptionText || activity.description)
              ? editedFields.description
              : undefined,
          recommendedDescriptionFormat:
            editedFields.description !== (activity.descriptionText || activity.description)
              ? ModernizedDescriptionFormat.Markdown
              : undefined,
          recommendedPointsPossible: editedFields.points,
          recommendedDueDate: editedFields.dueDate,
        },
      });

      if (result.data?.updateModernizedActivityFields?.success) {
        await refetch();
        onClose();
      }
    } catch (error) {
      console.error("Failed to save activity fields:", error);
    } finally {
      setIsSaving(false);
    }
  };

  if (loading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="small" color={colors.brand[600]} />
        <CustomText text="Loading entity details..." useTranslationText={false} size="xs" style={styles.loadingText} />
      </View>
    );
  }

  if (!activity) {
    return (
      <View style={styles.errorContainer}>
        <CustomText
          text="Could not load entity details"
          useTranslationText={false}
          size="xs"
          style={styles.errorText}
        />
        <Pressable onPress={onClose} style={styles.closeButton}>
          <CustomText text="Close" useTranslationText={false} size="xs" weight="medium" />
        </Pressable>
      </View>
    );
  }

  // Determine variant based on entity type
  const variant: SegmentVariant = normalizedEntityType === ModernizedActivityEntityType.Module ? "module" : "activity";

  const currentFields: StructuredFields = {
    title: activity.title ?? entityTitle,
    description: activity.descriptionText || activity.description || "",
    dueDate: activity.dueDate,
    points: activity.pointsPossible,
  };

  const proposedFields: StructuredFields = {
    title: activity.recommendedTitle ?? activity.title ?? entityTitle,
    description: activity.recommendedDescription ?? activity.descriptionText ?? activity.description ?? "",
    dueDate: activity.recommendedDueDate ?? activity.dueDate,
    points: activity.recommendedPointsPossible ?? activity.pointsPossible,
  };

  return (
    <View style={styles.editorContainer}>
      <View style={styles.editorHeader}>
        <View style={styles.headerTitleGroup}>
          <PencilSimpleIcon size={16} color={colors.brand[600]} weight="bold" />
          <CustomText text="Edit" useTranslationText={false} size="s" weight="bold" style={styles.editorHeaderTitle} />
        </View>
        <Pressable
          accessibilityRole="button"
          accessibilityLabel="Close editor"
          onPress={onClose}
          style={styles.closeIconButton}>
          <XIcon size={16} color={colors.grey[500]} />
        </Pressable>
      </View>

      <SegmentedDiffView
        variant={variant}
        currentFields={currentFields}
        proposedFields={proposedFields}
        isEditDisabled={isSaving}
        onSaveEdit={handleSave}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  editorContainer: {
    backgroundColor: colors.grey[50],
    borderRadius: 12,
    padding: 16,
    marginTop: 12,
    gap: 12,
  },
  editorHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  headerTitleGroup: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
  },
  editorHeaderTitle: {
    color: colors.brand[600],
  },
  closeIconButton: {
    padding: 4,
  },
  loadingContainer: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    padding: 16,
    backgroundColor: colors.grey[50],
    borderRadius: 12,
    marginTop: 12,
  },
  loadingText: {
    color: colors.grey[600],
  },
  errorContainer: {
    padding: 16,
    backgroundColor: colors.error[50],
    borderRadius: 12,
    marginTop: 12,
    gap: 8,
  },
  errorText: {
    color: colors.error[700],
  },
  closeButton: {
    alignSelf: "flex-start",
    paddingHorizontal: 12,
    paddingVertical: 6,
    backgroundColor: colors.white,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: colors.error[300],
  },
});

export default GapAnalysisIssueEditor;
